import { useEffect, useMemo, useState } from "react"
import {
  hasClientAuthToken,
  reconcileClientAuthSession,
  subscribeAuthSession,
} from "@/lib/authSession"
import { isInLineLiffClient } from "@/lib/openLineExternalBrowser"

type AuthSnapshot = {
  hasToken: boolean
  hasLineProfileInLiff: boolean
}

/** SSR + first client paint must match; real token is applied in `useEffect` + auth events. */
const INITIAL_AUTH_SNAPSHOT: AuthSnapshot = {
  hasToken: false,
  hasLineProfileInLiff: false,
}

function readAuthSnapshot(): AuthSnapshot {
  if (typeof window === "undefined") return { hasToken: false, hasLineProfileInLiff: false }
  const hasToken = hasClientAuthToken()
  let hasLineProfileInLiff = false
  try {
    if (isInLineLiffClient()) {
      const lp = window.localStorage.getItem("line_profile")
      hasLineProfileInLiff = typeof lp === "string" && lp.trim().length > 0
    }
  } catch {
    // ignore
  }
  return { hasToken, hasLineProfileInLiff }
}

/**
 * Single source of truth for "logged in" state.
 *
 * Rules:
 * - If `auth_token` is missing, treat as logged out immediately (even if NextAuth lags).
 * - When token exists, treat as logged in for UI gating.
 *
 * NOTE: LIFF may have a cached LINE profile (`line_profile`) even when the user is logged out.
 * That profile is only used for showing a friendly "confirm/connect" UI, not for auth gating.
 */
export function useAuthState() {
  const [snap, setSnap] = useState<AuthSnapshot>(INITIAL_AUTH_SNAPSHOT)
  const [isAuthReady, setIsAuthReady] = useState(false)

  useEffect(() => {
    const sync = () => {
      if (reconcileClientAuthSession()) {
        window.dispatchEvent(new Event("auth:logout"))
      }
      setSnap(readAuthSnapshot())
    }
    sync()
    setIsAuthReady(true)

    const onVisible = () => {
      if (!document.hidden) sync()
    }
    document.addEventListener("visibilitychange", onVisible)

    const unsub = subscribeAuthSession(sync)
    return () => {
      document.removeEventListener("visibilitychange", onVisible)
      unsub()
    }
  }, [])

  // We intentionally do NOT depend on NextAuth session fetch here.
  // In some environments (e.g. LINE/LIFF webview), session fetch can fail and
  // trigger noisy ClientFetchError overlays in dev, even though our app auth
  // is driven by `auth_token` in localStorage.
  const isLoadingSession = !isAuthReady
  const isLoggedIn = useMemo(() => {
    return snap.hasToken
  }, [snap.hasToken])

  return { isLoggedIn, isLoadingSession, isAuthReady, sessionStatus: "unknown" as const }
}

